import { Image, Loader } from '@storybook';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';

import { API_URL, MESSAGE } from 'constant';
import { loginState } from 'global-stores';
import { useNetwork, useNotification } from 'hooks';
import { convertFileToBase64 } from 'utils';
import { PERMISSION_SCREEN, useUserRoles } from 'views';

import {
	ChangeRequest,
	ChangeType,
	Side,
	digitsOf,
	formatPhoneInput,
	formatTtl,
	useProfileChanges,
} from './use-profile-changes';

const COUNTRY_CODES = ['+1', '+44', '+91', '+61', '+49', '+33', '+81', '+65'];

/**
 * THE ACCOUNT CARD — identity, and the approvals that guard it.
 *
 * Both editable fields are now approve-by-email: a change to the phone number
 * or the sign-in address is a REQUEST, not a write. Nothing reaches the user
 * record until a link in an emailed envelope is clicked, so every field here
 * has a third form beyond "row" and "editor" — a pending ticket.
 *
 * Design of record: `_design/specs/17-profile-change-ui.md`. The rules that
 * hold the layout together, all of them load-bearing at 272px:
 *   • one editor open at a time (height, and focus)
 *   • a ticket REPLACES its field's row and editor — pending never stacks on
 *     top of an input
 *   • the spam instruction renders once, card-level, whenever anything is
 *     pending — not once per ticket
 * The footer is `flex: 0 0 auto` and this card carries no `calc(100vh …)`;
 * §3.8 of the spec budgets the worst state against a 500px viewport.
 */
/* ── the pending slab ─────────────────────────────────────────────────── */
const Ticket = ({
	req,
	canWrite,
	onResend,
	onCancel,
}: {
	req: ChangeRequest;
	canWrite: boolean;
	onResend: (side: Side) => Promise<any>;
	onCancel: () => Promise<any>;
}) => {
	const [sent, setSent] = useState<Side | null>(null);
	const [busy, setBusy] = useState<Side | null>(null);

	const label = req.type === 'email' ? 'Email' : 'Phone';
	const sides = req.sides;

	/* The head line answers "which envelope is still unclicked" in words, so
	   the two-sided case needs no extra row for it. */
	const phrase = useMemo(() => {
		if (!sides) return 'awaiting approval';
		if (sides.current === 'approved') return 'waiting on new address';
		if (sides.next === 'approved') return 'waiting on current address';
		return 'awaiting approval';
	}, [sides]);

	const fire = useCallback(
		async (side: Side) => {
			setBusy(side);
			try {
				const res = await onResend(side);
				if (res?.success) {
					setSent(side);
					setTimeout(() => setSent(null), 3000);
				}
			} finally {
				setBusy(null);
			}
		},
		[onResend]
	);

	/* DISABLED, NOT ABSENT, DURING THE COOLDOWN.
	   Hiding it meant Resend was missing for the first 60 seconds after a
	   request — exactly when someone who did not get the mail goes looking for
	   it — and a control that appears and disappears reads as broken rather
	   than as rate-limited. It stays put and greys out. */
	const resendBtn = (side: Side) => {
		if (!canWrite) return null;
		const cooling = req.canResend?.[side] === false;
		return (
			<button
				type="button"
				className="sidebar__account-card-side-resend"
				disabled={cooling || busy === side}
				title={
					cooling
						? 'Just sent — you can resend again in a moment'
						: 'Re-sends this email and restarts the 2-hour window'
				}
				onClick={() => fire(side)}
			>
				{sent === side ? 'Sent ✓' : busy === side ? '…' : 'Resend'}
			</button>
		);
	};

	return (
		<div className="sidebar__account-card-ticket" role="status">
			<div className="sidebar__account-card-ticket-head">
				<span>
					{label} · {phrase}
				</span>
				<span className="sidebar__account-card-ttl" aria-hidden="true">
					{formatTtl(req.expiresAt)}
				</span>
			</div>

			<div className="sidebar__account-card-delta">
				<div className="sidebar__account-card-delta-row">
					<span className="sidebar__account-card-delta-k">From</span>
					<span className="sidebar__account-card-delta-v" title={req.current}>
						{req.current}
					</span>
				</div>
				<div className="sidebar__account-card-delta-row">
					<span className="sidebar__account-card-delta-k">To</span>
					<span className="sidebar__account-card-delta-v" title={req.next}>
						{req.next}
					</span>
				</div>
			</div>

			{/* Per-envelope progress AND per-envelope Resend: the user may have
			    received one mail and not the other, so a single button that
			    re-sends both is the wrong affordance. */}
			{sides && (
				<div className="sidebar__account-card-sides">
					{(['current', 'next'] as Side[]).map(side => {
						const done = sides[side] === 'approved';
						return (
							<div
								key={side}
								className={`sidebar__account-card-side sidebar__account-card-side--${
									done ? 'done' : 'wait'
								}`}
							>
								<span className="sidebar__account-card-dot" />
								<span>
									{done ? 'approved' : side === 'current' ? 'approve' : 'confirm'} —{' '}
									{side === 'current' ? 'current' : 'new'}
								</span>
								{!done && resendBtn(side)}
							</div>
						);
					})}
				</div>
			)}

			<div className="sidebar__account-card-ticket-foot">
				{!sides && resendBtn('current')}
				{canWrite && (
					<button
						type="button"
						className="sidebar__account-card-quiet sidebar__account-card-quiet--danger"
						onClick={onCancel}
					>
						Cancel request
					</button>
				)}
			</div>
		</div>
	);
};

export const AccountCard = () => {
	const [userDetail, setUserDetail] = useRecoilState(loginState);
	const {
		patch: patchUserProfile,
		remove: removeImage,
		get: getUser,
		loading: isUserImageLoaded,
	} = useNetwork();
	const { errorNotification, successNotification } = useNotification();
	const { checkUserWritePermission } = useUserRoles();
	const { byType, expired, request, resend, cancel, refresh } =
		useProfileChanges();

	const { countryCode, phone, image, email, isVerifiedPhone, firstName, lastName } =
		userDetail as any;

	const canWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.UserProfile),
		[checkUserWritePermission]
	);

	/* Which editor is open — never both. */
	const [open, setOpen] = useState<ChangeType | null>(null);
	const [draft, setDraft] = useState('');
	const [draftCc, setDraftCc] = useState(countryCode || '+1');
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState('');
	const [flash, setFlash] = useState<ChangeType | null>(null);

	/* NAME IS THE ONE FIELD HERE THAT SAVES STRAIGHT THROUGH.
	   Nothing authenticates against a display name and nothing is delivered to
	   it, so there is no threat for an approval round to answer — gating it
	   behind an emailed link would be ceremony for its own sake. It gets its own
	   small editor rather than joining the approve-by-email machinery. */
	const [nameOpen, setNameOpen] = useState(false);
	const [draftFirst, setDraftFirst] = useState('');
	const [draftLast, setDraftLast] = useState('');
	const [savingName, setSavingName] = useState(false);

	/* RECONCILE THE USER RECORD, DON'T INFER FROM TRANSITIONS.
	   This first watched for a pending request going present→absent, which is
	   wrong twice over: the request vanishes on cancel as well as on apply, and
	   a transition that happens while this component is unmounted (or the tab is
	   backgrounded and not polling) is never observed at all — so the sidebar
	   sat on identity it read at login and only a manual reload fixed it.
	   Comparing against the server instead is self-healing: it does not care
	   how or when the change happened, only whether what is on screen still
	   matches what is stored. */
	const syncUser = useCallback(async () => {
		if (!userDetail?.userId) return;
		const resp: any = await getUser(
			`${API_URL.CLIENT_SIGNUP}/${userDetail.userId}`
		);
		const fresh = resp?.data ?? resp;
		const server = Array.isArray(fresh) ? fresh[0] : fresh;
		if (!server?.email) return;

		const emailMoved = String(server.email) !== String(email ?? '');
		const phoneMoved = String(server.phone ?? '') !== String(phone ?? '');
		const flagsMoved =
			server.isVerifiedPhone !== isVerifiedPhone ||
			server.isVerifiedEmail !== (userDetail as any).isVerifiedEmail;
		if (!emailMoved && !phoneMoved && !flagsMoved) return;

		setUserDetail((prev: any) => ({
			...prev,
			email: server.email,
			phone: server.phone,
			countryCode: server.countryCode,
			isVerifiedEmail: server.isVerifiedEmail,
			isVerifiedPhone: server.isVerifiedPhone,
		}));
		if (emailMoved || phoneMoved) {
			setFlash(emailMoved ? 'email' : 'phone');
			setTimeout(() => setFlash(null), 4000);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userDetail?.userId, email, phone, isVerifiedPhone]);

	/* On mount, whenever the pending set changes, and on tab focus. Focus is the
	   one that matters most in practice: approving happens in the tab the email
	   opened, and coming back to this one is the moment the answer is wanted. */
	useEffect(() => {
		syncUser();
		window.addEventListener('focus', syncUser);
		return () => window.removeEventListener('focus', syncUser);
	}, [syncUser, byType.email, byType.phone]);

	/* SAME-BROWSER APPROVALS LAND IMMEDIATELY.
	   The confirm page posts here when it commits, so approving in another tab
	   updates this one at once rather than on the next poll — the whole benefit
	   people want from a socket, for the case that actually happens, without a
	   socket client, a server namespace, or a connection to keep alive.
	   Cross-DEVICE approval still resolves on focus or the 30s poll. */
	useEffect(() => {
		if (typeof BroadcastChannel === 'undefined') return undefined;
		const ch = new BroadcastChannel('simplici-profile-change');
		ch.onmessage = () => {
			refresh();
			syncUser();
		};
		return () => ch.close();
	}, [refresh, syncUser]);

	const startEdit = useCallback(
		(type: ChangeType) => {
			if (!canWrite) return;
			setErr('');
			setNameOpen(false);
			setOpen(type);
			setDraft(
				type === 'email' ? String(email ?? '') : formatPhoneInput(phone, countryCode)
			);
			setDraftCc(countryCode || '+1');
		},
		[canWrite, email, phone, countryCode]
	);

	const closeEdit = useCallback(() => {
		setOpen(null);
		setDraft('');
		setErr('');
	}, []);

	const dirty = useMemo(() => {
		if (open === 'email')
			return (
				draft.trim().toLowerCase() !== String(email ?? '').trim().toLowerCase()
			);
		if (open === 'phone')
			return (
				digitsOf(draft) !== digitsOf(phone) || draftCc !== (countryCode || '+1')
			);
		return false;
	}, [open, draft, draftCc, email, phone, countryCode]);

	const valid = useMemo(() => {
		if (open === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.trim());
		if (open === 'phone') {
			const d = digitsOf(draft);
			return d.length >= 7 && d.length <= 15;
		}
		return false;
	}, [open, draft]);

	const submit = useCallback(async () => {
		if (!open || !dirty || !valid) return;
		setBusy(true);
		setErr('');
		try {
			const body =
				open === 'email'
					? { type: 'email', email: draft.trim().toLowerCase() }
					: { type: 'phone', phone: digitsOf(draft), countryCode: draftCc };
			const res = await request(body);
			if (res?.success) {
				successNotification(res.message ?? 'Check your email to approve.');
				closeEdit();
			} else {
				setErr(res?.message ?? MESSAGE.ERROR);
			}
		} finally {
			setBusy(false);
		}
	}, [open, dirty, valid, draft, draftCc, request, successNotification, closeEdit]);

	const startName = useCallback(() => {
		if (!canWrite) return;
		setOpen(null);
		setErr('');
		setDraftFirst(String(firstName ?? ''));
		setDraftLast(String(lastName ?? ''));
		setNameOpen(true);
	}, [canWrite, firstName, lastName]);

	const nameDirty =
		draftFirst.trim() !== String(firstName ?? '').trim() ||
		draftLast.trim() !== String(lastName ?? '').trim();
	const nameValid = !!draftFirst.trim() && !!draftLast.trim();

	const saveName = useCallback(async () => {
		if (!nameDirty || !nameValid) return;
		setSavingName(true);
		try {
			const res: any = await patchUserProfile(
				`${API_URL.CLIENT_SIGNUP}/${userDetail.userId}`,
				{ firstName: draftFirst.trim(), lastName: draftLast.trim() }
			);
			if (res?.success !== false) {
				setUserDetail((prev: any) => ({
					...prev,
					firstName: draftFirst.trim(),
					lastName: draftLast.trim(),
					name: `${draftFirst.trim()} ${draftLast.trim()}`,
				}));
				successNotification('Name updated.');
				setNameOpen(false);
			} else {
				errorNotification(res?.message ?? MESSAGE.ERROR);
			}
		} finally {
			setSavingName(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [draftFirst, draftLast, nameDirty, nameValid, userDetail.userId]);

	/* ── avatar: unchanged plumbing, moved here from the old popover ── */
	const handleChangeImage = useCallback(
		async (e: ChangeEvent<HTMLInputElement> | any) => {
			const file = e.target.files[0];
			const selectedImage = (await convertFileToBase64(file)) as string;
			if (selectedImage) {
				patchUserProfile(`${API_URL.CLIENT_SIGNUP}/${userDetail.userId}`, {
					image: selectedImage,
				}).then((res: any) => {
					const { image: img, success, message } = res ?? {};
					if (success && img) {
						setUserDetail((prev: any) => ({
							...prev,
							image: URL.createObjectURL(e.target.files[0]),
						}));
					} else {
						errorNotification(message ?? MESSAGE.ERROR);
					}
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[patchUserProfile, userDetail.userId]
	);

	const handleRemoveImage = useCallback(() => {
		removeImage(`${API_URL.CLIENT_SIGNUP}/${userDetail.userId}`).then(() => {
			setUserDetail((prev: any) => ({ ...prev, image: '' }));
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userDetail.userId]);

	const face = useMemo(
		() => ({
			...(image && { url: image }),
			...(!image && { fileName: 'AvatarDefault-Light.svg' }),
		}),
		[image]
	);

	const anythingPending = !!(byType.email || byType.phone);

	/* ── one field, in whichever of its three forms applies ── */
	const renderField = (type: ChangeType) => {
		const pending = byType[type];
		const dead = expired[type];
		const label = type === 'email' ? 'Email' : 'Phone';
		const value =
			type === 'email'
				? String(email ?? '—')
				: `${countryCode || '+1'} ${formatPhoneInput(phone, countryCode)}`;

		if (pending)
			return (
				<Ticket
					key={type}
					req={pending}
					canWrite={canWrite}
					onResend={side => resend(pending, side)}
					onCancel={() => cancel(pending)}
				/>
			);

		if (dead)
			return (
				<div
					className="sidebar__account-card-ticket sidebar__account-card-ticket--expired"
					key={type}
					role="status"
				>
					<div className="sidebar__account-card-ticket-head">
						<span>
							{label} · request expired
						</span>
					</div>
					<div className="sidebar__account-card-delta">
						<div className="sidebar__account-card-delta-row">
							<span className="sidebar__account-card-delta-k">Wanted</span>
							<span className="sidebar__account-card-delta-v" title={dead.next}>
								{dead.next}
							</span>
						</div>
					</div>
					<div className="sidebar__account-card-ticket-foot">
						<button
							type="button"
							className="sidebar__account-card-quiet"
							onClick={() => {
								cancel(dead);
								startEdit(type);
							}}
						>
							Start again
						</button>
						<button
							type="button"
							className="sidebar__account-card-quiet"
							onClick={() => cancel(dead)}
						>
							Dismiss
						</button>
					</div>
				</div>
			);

		if (open === type)
			return (
				<div
					className={`sidebar__account-card-editor${
						busy ? ' sidebar__account-card-editor--busy' : ''
					}`}
					key={type}
				>
					<label className="sidebar__account-card-k" htmlFor={`sv2-acct-${type}`}>
						{label}
					</label>
					<div className="sidebar__account-card-editor-row">
						{type === 'phone' && (
							<select
								className="sidebar__account-card-ccsel"
								value={draftCc}
								disabled={busy}
								aria-label="Country code"
								onChange={e => setDraftCc(e.target.value)}
							>
								{COUNTRY_CODES.map(c => (
									<option key={c} value={c}>
										{c}
									</option>
								))}
							</select>
						)}
						<input
							id={`sv2-acct-${type}`}
							className="sidebar__account-card-num"
							value={draft}
							disabled={busy}
							autoFocus
							inputMode={type === 'phone' ? 'tel' : 'email'}
							placeholder={type === 'phone' ? '(555) 010-4477' : 'you@company.com'}
							onChange={e =>
								setDraft(
									type === 'phone'
										? formatPhoneInput(e.target.value, draftCc)
										: e.target.value
								)
							}
							onKeyDown={e => {
								if (e.key === 'Enter' && dirty && valid && !busy) submit();
								if (e.key === 'Escape') closeEdit();
							}}
						/>
					</div>

					{err && <div className="sidebar__account-card-err">{err}</div>}

					{dirty && (
						<div className="sidebar__account-card-editor-foot">
							<span className="sidebar__account-card-hint">
								We&apos;ll email you a link to approve this.
							</span>
							<div className="sidebar__account-card-editor-acts">
								<button
									type="button"
									className="sidebar__account-card-quiet"
									disabled={busy}
									onClick={closeEdit}
								>
									Cancel
								</button>
								<button
									type="button"
									className="sidebar__account-card-send"
									disabled={busy || !valid}
									onClick={submit}
								>
									{busy ? 'Sending…' : 'Request change'}
								</button>
							</div>
						</div>
					)}
				</div>
			);

		return (
			<button
				type="button"
				key={type}
				className="sidebar__account-card-frow"
				disabled={!canWrite}
				aria-label={`Edit ${label.toLowerCase()} — currently ${value}`}
				onClick={() => startEdit(type)}
			>
				<span className="sidebar__account-card-k">{label}</span>
				{canWrite && <span className="sidebar__account-card-fedit">Edit</span>}
				<span className="sidebar__account-card-fv" title={value}>
					{value}
				</span>
				{type === 'phone' && phone && isVerifiedPhone === false && (
					<span className="sidebar__account-card-fmeta">
						<span className="sidebar__account-card-chip sidebar__account-card-chip--wait">
							Unverified
						</span>
					</span>
				)}
				{flash === type && (
					<span className="sidebar__account-card-fmeta">
						<span className="sidebar__account-card-chip sidebar__account-card-chip--ok">
							Updated
						</span>
					</span>
				)}
			</button>
		);
	};

	return (
		<div className="sidebar__account-card" role="group" aria-label="Account">
			<div className="sidebar__account-card-face">
				{isUserImageLoaded ? (
					<span className="sidebar__account-card-avatar sidebar__account-card-avatar--busy">
						<Loader type="loader" dimension={18} />
					</span>
				) : (
					<Image {...face} className="sidebar__account-card-avatar" />
				)}
				{canWrite && (
					<div className="sidebar__account-card-actions">
						<label
							htmlFor="sv2-avatar-upload"
							className="sidebar__account-card-btn"
						>
							Change photo
						</label>
						<input
							id="sv2-avatar-upload"
							type="file"
							accept="image/png, image/jpeg"
							hidden
							onChange={handleChangeImage}
						/>
						{image && (
							<button
								type="button"
								className="sidebar__account-card-btn sidebar__account-card-btn--remove"
								onClick={handleRemoveImage}
							>
								Remove
							</button>
						)}
					</div>
				)}
			</div>

			{/* NAME — direct save, no approval. Sits above the identity fields
			    because it is the label for everything under it. */}
			{nameOpen ? (
				<div
					className={`sidebar__account-card-editor${
						savingName ? ' sidebar__account-card-editor--busy' : ''
					}`}
				>
					<label className="sidebar__account-card-k" htmlFor="sv2-acct-first">
						Name
					</label>
					<div className="sidebar__account-card-editor-row">
						<input
							id="sv2-acct-first"
							className="sidebar__account-card-num"
							value={draftFirst}
							disabled={savingName}
							autoFocus
							placeholder="First"
							aria-label="First name"
							onChange={e => setDraftFirst(e.target.value)}
							onKeyDown={e => {
								if (e.key === 'Enter' && nameDirty && nameValid) saveName();
								if (e.key === 'Escape') setNameOpen(false);
							}}
						/>
						<input
							className="sidebar__account-card-num"
							value={draftLast}
							disabled={savingName}
							placeholder="Last"
							aria-label="Last name"
							onChange={e => setDraftLast(e.target.value)}
							onKeyDown={e => {
								if (e.key === 'Enter' && nameDirty && nameValid) saveName();
								if (e.key === 'Escape') setNameOpen(false);
							}}
						/>
					</div>
					{nameDirty && (
						<div className="sidebar__account-card-editor-foot">
							<span className="sidebar__account-card-hint">
								Saves right away — no approval needed.
							</span>
							<div className="sidebar__account-card-editor-acts">
								<button
									type="button"
									className="sidebar__account-card-quiet"
									disabled={savingName}
									onClick={() => setNameOpen(false)}
								>
									Cancel
								</button>
								<button
									type="button"
									className="sidebar__account-card-send"
									disabled={savingName || !nameValid}
									onClick={saveName}
								>
									{savingName ? 'Saving…' : 'Save name'}
								</button>
							</div>
						</div>
					)}
				</div>
			) : (
				<button
					type="button"
					className="sidebar__account-card-frow"
					disabled={!canWrite}
					aria-label={`Edit name — currently ${firstName ?? ''} ${lastName ?? ''}`}
					onClick={startName}
				>
					<span className="sidebar__account-card-k">Name</span>
					{canWrite && <span className="sidebar__account-card-fedit">Edit</span>}
					<span className="sidebar__account-card-fv">
						{[firstName, lastName].filter(Boolean).join(' ') || '—'}
					</span>
				</button>
			)}

			{renderField('phone')}
			{renderField('email')}

			{/* ONE spam line for the whole card, not one per ticket. Approval mail
			    lands in spam often enough that the instruction has to be in the UI
			    — telling someone only inside the email they cannot find is no
			    help at all. */}
			{anythingPending && (
				<p className="sidebar__account-card-spam">
					Check your inbox — and your <b>spam folder</b>.
				</p>
			)}
		</div>
	);
};

